// Definition registry — the sim's read-only view of all content.

import { ABILITIES } from './abilities';
import {
  AD_ABILITIES,
  AD_BOSS_EXTRAS,
  AD_BUILDINGS,
  AD_CARDS,
  AD_RITUALS,
  AD_SPELLS,
  AD_TRAPS,
  AD_UNITS,
} from './astralDominion';
import {
  AH_ABILITIES,
  AH_BOSS_EXTRAS,
  AH_BUILDINGS,
  AH_CARDS,
  AH_RITUALS,
  AH_SPELLS,
  AH_TRAPS,
  AH_UNITS,
} from './ashenHost';
import { BUILDINGS } from './buildings';
import { CARDS } from './cards';
import { FACTIONS } from './factions';
import { MAPS } from './map';
import { RITUALS, SPELLS, TRAPS } from './spellsTraps';
import { BOSS_EXTRAS, UNITS } from './units';
import {
  VW_ABILITIES,
  VW_BOSS_EXTRAS,
  VW_BUILDINGS,
  VW_CARDS,
  VW_RITUALS,
  VW_SPELLS,
  VW_TRAPS,
  VW_UNITS,
} from './verdantWilds';
import type {
  AbilityDef,
  BossExtras,
  BuildingDef,
  CardDef,
  RitualDef,
  SpellDef,
  TrapDef,
  UnitDef,
} from './types';

function indexById<T extends { id: string }>(list: T[]): Map<string, T> {
  const m = new Map<string, T>();
  for (const item of list) {
    if (m.has(item.id)) throw new Error(`Duplicate content id: ${item.id}`);
    m.set(item.id, item);
  }
  return m;
}

export const defs = {
  units: indexById([...UNITS, ...VW_UNITS, ...AH_UNITS, ...AD_UNITS]),
  buildings: indexById([...BUILDINGS, ...VW_BUILDINGS, ...AH_BUILDINGS, ...AD_BUILDINGS]),
  cards: indexById([...CARDS, ...VW_CARDS, ...AH_CARDS, ...AD_CARDS]),
  abilities: indexById([...ABILITIES, ...VW_ABILITIES, ...AH_ABILITIES, ...AD_ABILITIES]),
  spells: indexById([...SPELLS, ...VW_SPELLS, ...AH_SPELLS, ...AD_SPELLS]),
  traps: indexById([...TRAPS, ...VW_TRAPS, ...AH_TRAPS, ...AD_TRAPS]),
  rituals: indexById([...RITUALS, ...VW_RITUALS, ...AH_RITUALS, ...AD_RITUALS]),
  bossExtras: {
    ...BOSS_EXTRAS,
    ...VW_BOSS_EXTRAS,
    ...AH_BOSS_EXTRAS,
    ...AD_BOSS_EXTRAS,
  } as Record<string, BossExtras | undefined>,
};

export function unitDef(id: string): UnitDef {
  const d = defs.units.get(id);
  if (!d) throw new Error(`Unknown unit def: ${id}`);
  return d;
}

export function buildingDef(id: string): BuildingDef {
  const d = defs.buildings.get(id);
  if (!d) throw new Error(`Unknown building def: ${id}`);
  return d;
}

export function cardDef(id: string): CardDef {
  const d = defs.cards.get(id);
  if (!d) throw new Error(`Unknown card def: ${id}`);
  return d;
}

export function abilityDef(id: string): AbilityDef {
  const d = defs.abilities.get(id);
  if (!d) throw new Error(`Unknown ability def: ${id}`);
  return d;
}

export function spellDef(id: string): SpellDef {
  const d = defs.spells.get(id);
  if (!d) throw new Error(`Unknown spell def: ${id}`);
  return d;
}

export function trapDef(id: string): TrapDef {
  const d = defs.traps.get(id);
  if (!d) throw new Error(`Unknown trap def: ${id}`);
  return d;
}

export function ritualDef(id: string): RitualDef {
  const d = defs.rituals.get(id);
  if (!d) throw new Error(`Unknown ritual def: ${id}`);
  return d;
}

/** Cross-reference validation, run once at boot (mirrors scripts/validate-content intent). */
export function validateContent(): string[] {
  const problems: string[] = [];
  const mapIds = new Set<string>();
  for (const map of Object.values(MAPS)) {
    if (mapIds.has(map.id)) problems.push(`duplicate map id ${map.id}`);
    mapIds.add(map.id);
    const playerCount = map.mode === '2v2' ? 4 : 2;
    if (map.corePositions.length !== playerCount) problems.push(`map ${map.id} has ${map.corePositions.length} spawns (want ${playerCount} for ${map.mode})`);
    if (map.spawnTeams.length !== playerCount) problems.push(`map ${map.id} has ${map.spawnTeams.length} spawn-team entries (want ${playerCount})`);
    const expectedTeams = map.mode === '2v2' ? [0, 0, 1, 1] : [0, 1];
    if (map.spawnTeams.some((team, index) => team !== expectedTeams[index])) problems.push(`map ${map.id} has invalid spawn-team distribution`);
    if (map.sites.length !== (map.mode === '2v2' ? 9 : 5)) problems.push(`map ${map.id} has ${map.sites.length} sites (want ${map.mode === '2v2' ? 9 : 5})`);
    const inBounds = (pos: { x: number; y: number }) => pos.x >= 0 && pos.y >= 0 && pos.x <= map.width && pos.y <= map.height;
    map.corePositions.forEach((pos, index) => { if (!inBounds(pos)) problems.push(`map ${map.id} spawn ${index} is out of bounds`); });
    const siteIds = new Set<string>();
    for (const site of map.sites) {
      if (siteIds.has(site.id)) problems.push(`map ${map.id} duplicates site ${site.id}`);
      siteIds.add(site.id);
      if (!inBounds(site.pos)) problems.push(`map ${map.id} site ${site.id} is out of bounds`);
    }
    map.deposits.forEach((deposit, index) => { if (!inBounds(deposit.pos)) problems.push(`map ${map.id} deposit ${index} is out of bounds`); });
    map.ruins.forEach((ruin, index) => { if (!inBounds(ruin.pos)) problems.push(`map ${map.id} ruin ${index} is out of bounds`); });
    for (const core of map.corePositions) {
      const nearby = map.deposits.filter((deposit) => Math.hypot(deposit.pos.x - core.x, deposit.pos.y - core.y) <= map.coreTerritoryRadius);
      const power = nearby.filter((deposit) => deposit.type === 'power').reduce((sum, deposit) => sum + deposit.amount, 0);
      const essence = nearby.filter((deposit) => deposit.type === 'essence').reduce((sum, deposit) => sum + deposit.amount, 0);
      if (power < 4000 || essence < 600) problems.push(`map ${map.id} spawn at ${core.x},${core.y} lacks standard starting deposits`);
    }
    const baseGroups = new Set(map.ruins.map((ruin) => ruin.group).filter((group): group is string => Boolean(group)));
    if (baseGroups.size < playerCount) problems.push(`map ${map.id} has ${baseGroups.size} base-wall groups (want at least ${playerCount})`);
  }
  for (const u of defs.units.values()) {
    for (const a of u.abilities) {
      if (!defs.abilities.has(a)) problems.push(`unit ${u.id} references missing ability ${a}`);
    }
    for (const host of u.mountableOn ?? []) {
      if (!defs.units.has(host)) problems.push(`unit ${u.id} mountableOn missing unit ${host}`);
    }
  }
  const subjectFaction = (c: CardDef): string | null => {
    switch (c.category) {
      case 'unit':
      case 'boss':
        return defs.units.get(c.subjectId)?.faction ?? null;
      case 'building':
        return defs.buildings.get(c.subjectId)?.faction ?? null;
      case 'spell':
        return defs.spells.get(c.subjectId)?.faction ?? null;
      case 'trap':
        return defs.traps.get(c.subjectId)?.faction ?? null;
      case 'ritual':
        return defs.rituals.get(c.subjectId)?.faction ?? null;
    }
  };
  for (const c of defs.cards.values()) {
    const sf = subjectFaction(c);
    if (sf === null) problems.push(`card ${c.id} references missing subject ${c.subjectId}`);
    else if (sf !== c.faction) problems.push(`card ${c.id} (${c.faction}) subject belongs to ${sf}`);
    if (c.category === 'unit' && c.requiresBuildings) {
      problems.push(`unit card ${c.id} cannot require a building`);
    }
    for (const bid of [...(c.requiresBuildings?.any ?? []), ...(c.requiresBuildings?.all ?? [])]) {
      const b = defs.buildings.get(bid);
      if (!b) problems.push(`card ${c.id} requires missing building ${bid}`);
      else if (b.faction !== c.faction) problems.push(`card ${c.id} requires foreign building ${bid}`);
    }
  }
  for (const b of defs.buildings.values()) {
    if ((b.roster?.entries.length ?? 0) > 2) problems.push(`building ${b.id} produces more than two unit types`);
    for (const e of b.roster?.entries ?? []) {
      const u = defs.units.get(e.unitId);
      if (!u) problems.push(`building ${b.id} roster references missing unit ${e.unitId}`);
      else if (u.faction !== b.faction) problems.push(`building ${b.id} roster crosses factions (${e.unitId})`);
    }
    // Core Annex System cross-references
    if (b.spawner && !defs.units.has(b.spawner.unitId)) {
      problems.push(`building ${b.id} spawner references missing unit ${b.spawner.unitId}`);
    }
    for (const up of b.upgrades ?? []) {
      if (up.spawner) {
        const u = defs.units.get(up.spawner.unitId);
        if (!u) problems.push(`annex ${up.id} spawner references missing unit ${up.spawner.unitId}`);
        else if (u.faction !== b.faction) problems.push(`annex ${up.id} spawner crosses factions (${up.spawner.unitId})`);
      }
      if (up.bloodTithe) {
        if (!defs.buildings.has(up.bloodTithe.cocoonBuildingId)) {
          problems.push(`annex ${up.id} tithe references missing cocoon ${up.bloodTithe.cocoonBuildingId}`);
        }
        for (const f of up.bloodTithe.forms) {
          if (!defs.units.has(f.unitId)) problems.push(`annex ${up.id} apotheosis form references missing unit ${f.unitId}`);
        }
        if (up.bloodTithe.forms.length === 0) problems.push(`annex ${up.id} tithe has no apotheosis forms`);
      }
    }
    // a Core with socketed annexes must fill each socket exactly once
    if (b.kind === 'core' && b.upgrades?.some((u) => u.socket)) {
      for (const socket of ['top', 'left', 'bottom'] as const) {
        const n = b.upgrades.filter((u) => u.socket === socket).length;
        if (n !== 1) problems.push(`core ${b.id} has ${n} annexes in the ${socket} socket (want 1)`);
      }
    }
  }
  for (const u of defs.units.values()) {
    if (u.deploysInto && !defs.buildings.has(u.deploysInto.buildingId)) {
      problems.push(`unit ${u.id} deploys into missing building ${u.deploysInto.buildingId}`);
    }
  }
  for (const t of defs.traps.values()) {
    if (t.eruptBuildingId && !defs.buildings.has(t.eruptBuildingId)) {
      problems.push(`trap ${t.id} erupts into missing building ${t.eruptBuildingId}`);
    }
  }
  for (const ab of defs.abilities.values()) {
    for (const eff of ab.effects) {
      if (eff.fx === 'spawnTemporary' && !defs.units.has(eff.unitId)) {
        problems.push(`ability ${ab.id} spawns missing unit ${eff.unitId}`);
      }
      if (eff.fx === 'plantSapling' && !defs.buildings.has(eff.buildingId)) {
        problems.push(`ability ${ab.id} plants missing building ${eff.buildingId}`);
      }
    }
  }
  for (const sp of defs.spells.values()) {
    for (const uid of sp.temporarySummon?.units ?? []) {
      if (!defs.units.has(uid)) problems.push(`spell ${sp.id} summons missing unit ${uid}`);
    }
  }
  // Non-Core building rework invariants: exactly 19 queue-based military/economic
  // producers, with one two-type roster per faction and every other roster specialized.
  const productionShape = {
    'iron-covenant': { producers: 5, one: 4, two: 1 },
    'ashen-host': { producers: 4, one: 3, two: 1 },
    'verdant-wilds': { producers: 5, one: 4, two: 1 },
    'astral-dominion': { producers: 5, one: 4, two: 1 },
  } as const;
  for (const [faction, want] of Object.entries(productionShape)) {
    const counts: number[] = [];
    for (const b of defs.buildings.values()) {
      if (b.faction !== faction || b.kind === 'core') continue;
      if (b.producesWorkers) counts.push(1);
      else if (b.roster && b.roster.entries.length > 0) counts.push(b.roster.entries.length);
    }
    const one = counts.filter((n) => n === 1).length;
    const two = counts.filter((n) => n === 2).length;
    if (counts.length !== want.producers || one !== want.one || two !== want.two) {
      problems.push(`${faction} production shape is ${counts.length}/${one}/${two}; want ${want.producers}/${want.one}/${want.two}`);
    }
  }
  // faction wiring: every implemented faction must resolve its def pointers and field
  // a legal 10-card test deck with exactly one boss per Ascension II/III/IV
  for (const fac of Object.values(FACTIONS)) {
    if (!fac.implemented) continue;
    for (const [what, id] of [
      ['core', fac.coreDefId],
      ['site tower', fac.siteTowerDefId],
      ['wall', fac.wallDefId],
      ['ritual standard', fac.ritualStandardDefId],
    ] as const) {
      if (!defs.buildings.has(id)) problems.push(`${fac.id} ${what} missing: ${id}`);
    }
    if (!defs.units.has(fac.workerUnitId)) problems.push(`${fac.id} worker missing: ${fac.workerUnitId}`);
    if (fac.defaultDeck.length !== 10) {
      problems.push(`${fac.id} test deck has ${fac.defaultDeck.length} cards (want 10)`);
    }
    const seen = new Set<string>();
    const bossTiers: number[] = [];
    let deckEligible = 0;
    for (const cid of fac.defaultDeck) {
      const c = defs.cards.get(cid);
      if (!c) {
        problems.push(`${fac.id} test deck references missing card ${cid}`);
        continue;
      }
      if (c.faction !== fac.id) problems.push(`${fac.id} test deck contains foreign card ${cid}`);
      if (seen.has(cid)) problems.push(`${fac.id} test deck duplicates ${cid}`);
      seen.add(cid);
      if (c.category === 'boss') bossTiers.push(c.ascension);
    }
    for (const c of defs.cards.values()) {
      if (c.faction === fac.id && c.deckEligible !== false) deckEligible++;
    }
    if (deckEligible < 10) problems.push(`${fac.id} card pool has only ${deckEligible} deck-eligible cards`);
    for (const tier of [2, 3, 4]) {
      if (!bossTiers.includes(tier)) problems.push(`${fac.id} test deck lacks an Ascension ${tier} boss`);
    }
  }
  return problems;
}
